import { useEffect, useMemo, useRef, useState } from 'react';
import { Crosshair, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGooglePlacesScript } from '@/hooks/useGooglePlacesScript';
import { jobWindow, type ScheduleJob } from '@/components/schedule/scheduleModel';
import type { FleetStatus, FleetTech } from '@/utils/fleetStatus';
import type { TrailPoint } from '@/hooks/useFleetLive';
import { MAP_ID, google, initials, resolveColour, useIsDarkMode, useMapsAuthFailed, type GoogleAny } from './googleMaps';

/** A technician as the map draws them: the fleet row, its derived status and lane colour. */
export interface MapTech {
  tech: FleetTech;
  status: FleetStatus;
  colour: string;
}

/** A service area (metro) drawn as a circle. */
export interface MapArea {
  id: string;
  name: string;
  lat: number;
  lng: number;
  radiusKm: number | null;
}

export interface MapView {
  center: { lat: number; lng: number };
  zoom: number;
}

interface FleetMapProps {
  jobs: ScheduleJob[];
  /** CSS colour for a job's technician (null = unassigned). */
  colourOf: (techId: string | null) => string;
  techs?: MapTech[];
  trail?: TrailPoint[] | null;
  trailColour?: string;
  areas?: MapArea[];
  selectedJobId?: string | null;
  selectedTechId?: string | null;
  onSelectJob: (id: string) => void;
  onSelectTech?: (id: string) => void;
  /** Keep this technician in view as their position updates. */
  followTechId?: string | null;
  /** Where to look when there is nothing to fit (no jobs, no techs). */
  fallback: MapView;
  /** The map fits everything again whenever this changes (e.g. the day shown). */
  fitKey: string;
  gestureHandling?: 'greedy' | 'cooperative' | 'auto';
  className?: string;
}

interface MarkerSpec {
  key: string;
  position: { lat: number; lng: number };
  title: string;
  zIndex: number;
  /** Content is rebuilt only when this changes; position moves in place. */
  sig: string;
  render: () => HTMLElement;
  onClick: () => void;
}

interface MarkerEntry {
  marker: GoogleAny;
  sig: string;
  onClick: () => void;
}

const TONE_RING: Record<FleetStatus['tone'], string> = {
  success: 'ring-success',
  warning: 'ring-warning',
  info: 'ring-info',
  muted: 'ring-muted-foreground/40',
};

/** Visit order per technician by start time: pin labels 1, 2, 3… */
function visitOrder(jobs: ScheduleJob[]): Map<string, number> {
  const byTech = new Map<string, ScheduleJob[]>();
  for (const job of jobs) {
    const key = job.assigned_tech_id ?? '';
    byTech.set(key, [...(byTech.get(key) ?? []), job]);
  }
  const result = new Map<string, number>();
  for (const list of byTech.values()) {
    list
      .sort((a, b) => (jobWindow(a)?.start ?? 1e4) - (jobWindow(b)?.start ?? 1e4))
      .forEach((job, i) => result.set(job.id, i + 1));
  }
  return result;
}

type JobPinState = 'open' | 'active' | 'done';

function jobState(job: ScheduleJob): JobPinState {
  if (job.status === 'completed') return 'done';
  if ((job.en_route_at || job.arrived_at) && !job.work_finished_at) return 'active';
  return 'open';
}

function compactAge(minutes: number | null): string {
  if (minutes == null) return '';
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h`;
}

function jobPin(o: { colour: string; label: string; state: JobPinState; selected: boolean; unassigned: boolean }): HTMLElement {
  const el = document.createElement('div');
  const size = o.selected ? 'h-8 w-8 text-xs' : 'h-6 w-6 text-[11px]';
  el.className = [
    'flex items-center justify-center rounded-full border-2 font-semibold shadow-md',
    size,
    o.unassigned ? 'border-dashed border-muted-foreground bg-card text-foreground' : 'border-white text-white',
    o.state === 'done' ? 'opacity-50' : '',
    o.state === 'active' ? 'ring-2 ring-info ring-offset-1' : '',
    o.selected ? 'outline outline-2 outline-foreground' : '',
  ].join(' ');
  if (!o.unassigned) el.style.background = o.colour;
  el.style.transform = `translateY(${o.selected ? 16 : 12}px)`; // centre on the address, not above it
  el.textContent = o.label;
  return el;
}

function techPin(o: {
  name: string; colour: string; tone: FleetStatus['tone']; stale: boolean; fromJob: boolean; selected: boolean; caption: string;
}): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = `flex flex-col items-center gap-0.5 ${o.stale ? 'opacity-60' : ''}`;
  wrap.style.transform = 'translateY(36px)'; // badge centre sits on the position
  const badge = document.createElement('div');
  badge.className = [
    'flex h-9 w-9 items-center justify-center rounded-full border-2 border-white text-xs font-bold text-white shadow-lg ring-[3px]',
    TONE_RING[o.tone],
    o.fromJob ? 'border-dashed' : '',
    o.selected ? 'scale-125' : '',
  ].join(' ');
  badge.style.background = o.colour;
  badge.textContent = initials(o.name);
  const label = document.createElement('div');
  label.className = 'whitespace-nowrap rounded bg-card/95 px-1 text-[10px] font-medium leading-4 text-foreground shadow';
  label.textContent = o.caption;
  wrap.append(badge, label);
  return wrap;
}

function syncMarkers(map: GoogleAny, store: Map<string, MarkerEntry>, specs: MarkerSpec[]) {
  const g = google();
  const wanted = new Set(specs.map((s) => s.key));
  for (const [key, entry] of store) {
    if (!wanted.has(key)) {
      entry.marker.map = null;
      store.delete(key);
    }
  }
  for (const s of specs) {
    const entry = store.get(s.key);
    if (entry) {
      entry.marker.position = s.position;
      entry.marker.zIndex = s.zIndex;
      entry.marker.title = s.title;
      entry.onClick = s.onClick;
      if (entry.sig !== s.sig) {
        entry.marker.content = s.render();
        entry.sig = s.sig;
      }
    } else {
      const marker = new g.maps.marker.AdvancedMarkerElement({
        map, position: s.position, content: s.render(), title: s.title, zIndex: s.zIndex, gmpClickable: true,
      });
      const created: MarkerEntry = { marker, sig: s.sig, onClick: s.onClick };
      marker.addListener('click', () => created.onClick());
      store.set(s.key, created);
    }
  }
}

/** Light/dark is fixed when a Google map is created, so the map remounts with the theme. */
export function FleetMap(props: FleetMapProps) {
  const dark = useIsDarkMode();
  return <FleetMapInner key={dark ? 'dark' : 'light'} {...props} dark={dark} />;
}

function FleetMapInner({
  jobs, colourOf, techs = [], trail, trailColour = 'hsl(var(--primary))', areas = [],
  selectedJobId = null, selectedTechId = null, onSelectJob, onSelectTech, followTechId = null,
  fallback, fitKey, gestureHandling = 'auto', className, dark,
}: FleetMapProps & { dark: boolean }) {
  const { isReady, loadError } = useGooglePlacesScript();
  const authFailed = useMapsAuthFailed();
  const hasKey = !!import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleAny>(null);
  const [mapReady, setMapReady] = useState(false);
  const jobMarkers = useRef(new Map<string, MarkerEntry>());
  const techMarkers = useRef(new Map<string, MarkerEntry>());
  const halos = useRef(new Map<string, GoogleAny>());
  const routeLines = useRef(new Map<string, GoogleAny>());
  const trailLine = useRef<GoogleAny>(null);
  const areaCircles = useRef<GoogleAny[]>([]);
  const fitted = useRef<{ key: string; count: number } | null>(null);
  const handlers = useRef({ onSelectJob, onSelectTech });
  handlers.current = { onSelectJob, onSelectTech };

  const mapped = useMemo(() => jobs.filter((j) => j.lat != null && j.lng != null), [jobs]);
  const unmapped = jobs.length - mapped.length;
  const order = useMemo(() => visitOrder(mapped), [mapped]);
  const placed = useMemo(() => techs.filter((t) => t.status.position), [techs]);

  // ── Create the map once ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isReady || mapRef.current || !containerRef.current) return;
    let cancelled = false;
    (async () => {
      const g = google();
      const { Map } = await g.maps.importLibrary('maps');
      await g.maps.importLibrary('marker');
      if (cancelled || !containerRef.current) return;
      const scheme = g.maps.ColorScheme;
      mapRef.current = new Map(containerRef.current, {
        center: fallback.center,
        zoom: fallback.zoom,
        mapId: MAP_ID,
        ...(scheme ? { colorScheme: dark ? scheme.DARK : scheme.LIGHT } : {}),
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
        gestureHandling,
      });
      setMapReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- created once; later props are applied by the effects below
  }, [isReady]);

  // ── Jobs ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const specs: MarkerSpec[] = mapped.map((job) => {
      const colour = colourOf(job.assigned_tech_id);
      const selected = job.id === selectedJobId;
      const label = String(order.get(job.id) ?? '');
      const state = jobState(job);
      const unassigned = !job.assigned_tech_id;
      return {
        key: job.id,
        position: { lat: Number(job.lat), lng: Number(job.lng) },
        title: [job.job_number, job.customer_name, job.customer_suburb].filter(Boolean).join(' · '),
        zIndex: selected ? 900 : state === 'done' ? 1 : 10,
        sig: [colour, selected, label, state, unassigned].join('|'),
        render: () => jobPin({ colour, label, state, selected, unassigned }),
        onClick: () => handlers.current.onSelectJob(job.id),
      };
    });
    syncMarkers(mapRef.current, jobMarkers.current, specs);
  }, [mapReady, mapped, order, colourOf, selectedJobId]);

  // ── Technicians: badge, accuracy halo, line to the job they're driving to ──
  useEffect(() => {
    if (!mapReady) return;
    const g = google();
    const map = mapRef.current;

    const specs: MarkerSpec[] = placed.map(({ tech, status, colour }) => {
      const p = status.position as NonNullable<FleetStatus['position']>;
      const selected = tech.tech_id === selectedTechId;
      const caption = p.source === 'job' ? 'at job' : compactAge(p.ageMinutes);
      return {
        key: tech.tech_id,
        position: { lat: p.lat, lng: p.lng },
        title: `${tech.name} · ${status.activityLabel} · ${status.locationLabel}`,
        zIndex: selected ? 1000 : 500,
        sig: [colour, selected, status.tone, status.stale, p.source, tech.name, caption].join('|'),
        render: () => techPin({ name: tech.name, colour, tone: status.tone, stale: status.stale, fromJob: p.source === 'job', selected, caption }),
        onClick: () => handlers.current.onSelectTech?.(tech.tech_id),
      };
    });
    syncMarkers(map, techMarkers.current, specs);

    const haloFor = new Map(
      placed
        .filter((t) => t.status.position?.source === 'fix' && (t.status.position.accuracyM ?? 0) > 0)
        .map((t) => [t.tech.tech_id, t]),
    );
    for (const [key, circle] of halos.current) {
      if (!haloFor.has(key)) { circle.setMap(null); halos.current.delete(key); }
    }
    for (const [key, t] of haloFor) {
      const p = t.status.position as NonNullable<FleetStatus['position']>;
      const colour = resolveColour(t.colour);
      const options = {
        center: { lat: p.lat, lng: p.lng },
        radius: Math.min(p.accuracyM ?? 0, 3000),
        fillColor: colour,
        fillOpacity: t.status.stale ? 0.05 : 0.12,
        strokeColor: colour,
        strokeOpacity: 0.35,
        strokeWeight: 1,
        clickable: false,
      };
      const existing = halos.current.get(key);
      if (existing) existing.setOptions(options);
      else halos.current.set(key, new g.maps.Circle({ ...options, map }));
    }

    const lineFor = new Map(
      placed
        .filter((t) => t.status.activity === 'en_route' && t.status.activeJob?.lat != null && t.status.activeJob?.lng != null)
        .map((t) => [t.tech.tech_id, t]),
    );
    for (const [key, line] of routeLines.current) {
      if (!lineFor.has(key)) { line.setMap(null); routeLines.current.delete(key); }
    }
    for (const [key, t] of lineFor) {
      const p = t.status.position as NonNullable<FleetStatus['position']>;
      const job = t.status.activeJob as NonNullable<FleetStatus['activeJob']>;
      const options = {
        path: [{ lat: p.lat, lng: p.lng }, { lat: Number(job.lat), lng: Number(job.lng) }],
        strokeOpacity: 0,
        icons: [{
          icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeColor: resolveColour(t.colour), scale: 3 },
          offset: '0',
          repeat: '14px',
        }],
        clickable: false,
        zIndex: 5,
      };
      const existing = routeLines.current.get(key);
      if (existing) existing.setOptions(options);
      else routeLines.current.set(key, new g.maps.Polyline({ ...options, map }));
    }
  }, [mapReady, placed, selectedTechId]);

  // ── Trail (admin, one technician, one day) ─────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const g = google();
    if (!trail || trail.length < 2) {
      trailLine.current?.setMap(null);
      trailLine.current = null;
      return;
    }
    const options = {
      path: trail.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })),
      strokeColor: resolveColour(trailColour),
      strokeOpacity: 0.85,
      strokeWeight: 3,
      icons: [{ icon: { path: g.maps.SymbolPath.FORWARD_OPEN_ARROW, scale: 2 }, repeat: '90px' }],
      clickable: false,
      zIndex: 4,
    };
    if (trailLine.current) trailLine.current.setOptions(options);
    else trailLine.current = new g.maps.Polyline({ ...options, map: mapRef.current });
  }, [mapReady, trail, trailColour]);

  // ── Service areas ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const g = google();
    areaCircles.current.forEach((c) => c.setMap(null));
    const stroke = resolveColour('hsl(var(--muted-foreground))');
    areaCircles.current = areas
      .filter((a) => a.radiusKm && a.radiusKm > 0)
      .map((a) => new g.maps.Circle({
        map: mapRef.current,
        center: { lat: a.lat, lng: a.lng },
        radius: (a.radiusKm as number) * 1000,
        strokeColor: stroke,
        strokeOpacity: 0.5,
        strokeWeight: 1,
        fillOpacity: 0,
        clickable: false,
      }));
  }, [mapReady, areas]);

  // ── Fit: when the day changes, or when the first data arrives ──────────────
  const points = useMemo(
    () => [
      ...mapped.map((j) => ({ lat: Number(j.lat), lng: Number(j.lng) })),
      ...placed.map((t) => ({ lat: (t.status.position as NonNullable<FleetStatus['position']>).lat, lng: (t.status.position as NonNullable<FleetStatus['position']>).lng })),
    ],
    [mapped, placed],
  );

  const fitTo = (pts: { lat: number; lng: number }[]) => {
    const map = mapRef.current;
    if (!map) return;
    if (pts.length === 0) {
      map.setCenter(fallback.center);
      map.setZoom(fallback.zoom);
    } else if (pts.length === 1) {
      map.setCenter(pts[0]);
      map.setZoom(13);
    } else {
      const bounds = new (google().maps.LatLngBounds)();
      pts.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, 56);
    }
  };

  useEffect(() => {
    if (!mapReady) return;
    const last = fitted.current;
    if (last && last.key === fitKey && (last.count > 0 || points.length === 0)) return;
    fitted.current = { key: fitKey, count: points.length };
    fitTo(points);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fitTo reads refs and the fallback
  }, [mapReady, fitKey, points]);

  // ── Pan to what was picked (from the map or the side panel) ────────────────
  useEffect(() => {
    if (!mapReady || !selectedJobId) return;
    const job = mapped.find((j) => j.id === selectedJobId);
    if (job) mapRef.current.panTo({ lat: Number(job.lat), lng: Number(job.lng) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the selection changes
  }, [mapReady, selectedJobId]);

  const positionOf = (techId: string | null) =>
    (techId ? placed.find((t) => t.tech.tech_id === techId)?.status.position : null) ?? null;

  // Selecting a technician pans to them once.
  useEffect(() => {
    const p = positionOf(selectedTechId);
    if (mapReady && p) mapRef.current.panTo({ lat: p.lat, lng: p.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the selection changes
  }, [mapReady, selectedTechId]);

  // Following pans on every new position.
  const followed = positionOf(followTechId);
  useEffect(() => {
    if (mapReady && followed) mapRef.current.panTo({ lat: followed.lat, lng: followed.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the coordinates themselves
  }, [mapReady, followTechId, followed?.lat, followed?.lng]);

  if (!hasKey || loadError || authFailed) {
    return (
      <div className={className ?? 'h-full min-h-[240px]'}>
        <MapMessage>
          {authFailed
            ? 'Google Maps refused the request. Check that billing is on for the Google Cloud project and that the key allows this site.'
            : 'The map could not load. Check the Google Maps key (VITE_GOOGLE_PLACES_API_KEY) allows the Maps JavaScript API.'}
        </MapMessage>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-lg border bg-muted ${className ?? 'h-full min-h-[420px]'}`}>
      <div ref={containerRef} className="absolute inset-0" />
      {mapReady && (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="absolute right-3 top-14 h-9 w-9 shadow"
          aria-label="Fit everything on the map"
          title="Fit everything"
          onClick={() => fitTo(points)}
        >
          <Crosshair className="h-4 w-4" />
        </Button>
      )}
      {mapReady && mapped.length === 0 && placed.length === 0 && (
        <div className="absolute inset-x-3 top-3 rounded-md bg-card/95 px-3 py-2 text-sm shadow sm:right-auto">
          No jobs or technicians with a location to show.
        </div>
      )}
      {unmapped > 0 && (
        <div className="absolute bottom-3 left-3 rounded-md bg-card/95 px-3 py-1.5 text-xs text-muted-foreground shadow">
          {unmapped} job{unmapped === 1 ? '' : 's'} without a location not shown
        </div>
      )}
    </div>
  );
}

function MapMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 rounded-lg border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
      <MapPin className="h-8 w-8" />
      {children}
    </div>
  );
}
